import { parseBooleanFromString } from '@legacy/utils/boolean';
import { WebStorageStateStore } from 'oidc-client-ts';
import { PostHogConfig } from 'posthog-js';
import { ConfigurationOptions } from 'typesense/lib/Typesense/Configuration';

declare global {
  interface Window {
    RUNTIME_CONFIG: Config;
  }
}

interface Config {
  phoenixUrl: string;
  tlpUrl: string;
  loginPortal: string;
  environment: string;
  clientId: string;
  redirectUri: string;
  posthogHost: string;
  posthogKey: string;
  typesenseHostUrl: string;
  enableMock: string;
  [key: string]: string;
}

// Access runtime configuration (injected by env.sh). Guarded so importing this
// module in a Node context (Playwright test discovery, SSR-like tooling) does
// not throw — `window` and Vite's `import.meta.env` only exist in the browser
// build.
export const viteEnv =
  typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env
    : ({} as Record<string, string | undefined>);

export const config = (typeof window !== 'undefined' &&
  window.RUNTIME_CONFIG) || {
  phoenixUrl: viteEnv.VITE_PHOENIX_URL,
  tlpUrl: viteEnv.VITE_TLP_URL,
  loginPortal: viteEnv.VITE_LOGIN_PORTAL,
  environment: viteEnv.VITE_ENVIRONMENT,
  clientId: viteEnv.VITE_CLIENT_ID,
  redirectUri: viteEnv.VITE_REDIRECT_URI,
  posthogHost: viteEnv.VITE_POSTHOG_HOST,
  posthogKey: viteEnv.VITE_POSTHOG_KEY,
  typesenseHostUrl: viteEnv.VITE_TYPESENSE_HOST_URL,
  muiLicenseKey: viteEnv.VITE_MUI_LICENSE_KEY,
  enableMock: parseBooleanFromString(viteEnv.VITE_ENABLE_MOCK),
  bypassTLP: parseBooleanFromString(viteEnv.VITE_BYPASS_TLP),
};

export const oidcConfig = {
  authority: config?.loginPortal,
  client_id: config?.clientId,
  redirect_uri: config?.redirectUri,
  scope: 'openid profile tlp offline_access',
  automaticSilentRenew: true,
  ...(typeof window !== 'undefined' && {
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
  }),
};

export const posthogOptionsConfig: Partial<PostHogConfig> = {
  api_host: config.posthogHost,
  ui_host: 'https://eu.posthog.com',
  autocapture: false,
  disable_session_recording: true,
  capture_pageview: 'history_change', // pageleave is tracked by default when pageview is
  ...(config?.environment?.toLowerCase() !== 'production' && {
    capture_pageview: false,
    rageclick: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    disable_surveys: true,
  }),
};

// Per-collection scoped API keys are fetched at runtime from /Search/token
// (see useTypesenseAPIKey) and passed via the `x-typesense-api-key` header on
// each request, so the singleton client is initialised without a static key.
export const typesenseConfig: ConfigurationOptions = {
  nodes: [
    {
      url: config?.typesenseHostUrl || 'http://localhost:8108',
    },
  ],
  apiKey: '',
};
